import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  BookOpen, ArrowLeft, CheckCircle2, Loader2,
  BookMarked, List, Clock, Lightbulb, Target,
  FileText, ChevronLeft, ChevronRight, PenTool,
  Link2, ExternalLink, HelpCircle, CheckCircle, XCircle,
  Trophy, RefreshCw
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const FoundationChapterPage = () => {
  const { subjectId, bookId, chapterId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [relatedChapters, setRelatedChapters] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  
  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchContent();
    fetchRelatedChapters();
  }, [user, token, chapterId, navigate]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/foundation/chapters/${chapterId}/content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      
      const data = await res.json();
      setContent(data);
      
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load chapter content');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedChapters = async () => {
    setLoadingRelated(true);
    try {
      const res = await fetch(`${API_URL}/api/foundation/chapters/${chapterId}/related`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch related');
      
      const data = await res.json();
      setRelatedChapters(data.related_chapters || []);
      
    } catch (error) {
      console.error('Error fetching related chapters:', error);
      // Don't show error toast - related chapters are optional
    } finally {
      setLoadingRelated(false);
    }
  };

  const fetchQuiz = async () => {
    setLoadingQuiz(true);
    setQuizSubmitted(false);
    setQuizResults(null);
    setSelectedAnswers({});
    try {
      const res = await fetch(`${API_URL}/api/foundation/chapters/${chapterId}/quiz`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch quiz');
      
      const data = await res.json();
      setQuiz(data);
      
    } catch (error) {
      console.error('Error fetching quiz:', error);
      toast.error('Failed to load quiz. Please try again.');
    } finally {
      setLoadingQuiz(false);
    }
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    
    const answers = quiz.questions.map((_, idx) => selectedAnswers[idx] ?? -1);
    
    // Check if all questions are answered
    if (answers.includes(-1)) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    
    setSubmittingQuiz(true);
    try {
      const res = await fetch(`${API_URL}/api/foundation/chapters/${chapterId}/quiz/submit`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chapter_id: chapterId,
          answers: answers
        })
      });
      
      if (!res.ok) throw new Error('Failed to submit quiz');
      
      const data = await res.json();
      setQuizResults(data);
      setQuizSubmitted(true);
      
      if (data.percentage >= 80) {
        toast.success(`Excellent! You scored ${data.score}/${data.total} (${data.percentage}%)`);
      } else if (data.percentage >= 60) {
        toast.success(`Good job! You scored ${data.score}/${data.total} (${data.percentage}%)`);
      } else {
        toast.info(`You scored ${data.score}/${data.total}. Review the explanations to improve.`);
      }
      
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const resetQuiz = () => {
    setQuizSubmitted(false);
    setQuizResults(null);
    setSelectedAnswers({});
  };

  const markComplete = async () => {
    setMarking(true);
    try {
      const res = await fetch(`${API_URL}/api/foundation/progress?chapter_id=${chapterId}&completed=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to mark');
      
      toast.success('Chapter marked as complete!');
      
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error('Failed to mark chapter');
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />
        <p className="text-slate-600">Generating chapter content...</p>
        <p className="text-slate-400 text-sm">This may take 20-30 seconds for new chapters</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <p className="text-slate-500">Chapter not found</p>
      </div>
    );
  }

  const upscRelevance = content.upsc_relevance || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="foundation-chapter-page">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <Link 
              to={`/foundation/${subjectId}/book/${bookId}`} 
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-white/20 text-white border-0">
                  Class {content.class_num}
                </Badge>
                <Badge className="bg-white/10 text-white/80 border-0">
                  Chapter {content.chapter_num}
                </Badge>
              </div>
              <h1 className="text-xl md:text-2xl font-bold font-heading">
                {content.title}
              </h1>
            </div>
          </div>
          
          {/* Topics */}
          <div className="flex flex-wrap gap-2">
            {content.topics.map((topic, idx) => (
              <span 
                key={idx}
                className="px-2 py-1 bg-white/10 text-emerald-100 text-xs rounded"
              >
                {topic}
              </span>
            ))}
          </div>
          
          {/* UPSC Relevance Tags */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {upscRelevance.gs_papers?.map((paper, idx) => (
              <Badge key={idx} className="bg-blue-500/30 text-blue-100 border-0">
                {paper}
              </Badge>
            ))}
            {upscRelevance.prelims_important && (
              <Badge className="bg-orange-500/30 text-orange-100 border-0">
                Prelims Important
              </Badge>
            )}
            {upscRelevance.mains_important && (
              <Badge className="bg-purple-500/30 text-purple-100 border-0">
                Mains Important
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);
          // Load quiz when tab is clicked
          if (val === 'quiz' && !quiz && !loadingQuiz) {
            fetchQuiz();
          }
        }} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap shadow-sm">
            <TabsTrigger value="summary" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="keypoints" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <List className="h-4 w-4 mr-2" />
              Key Points
            </TabsTrigger>
            <TabsTrigger value="terms" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <BookMarked className="h-4 w-4 mr-2" />
              Terms
            </TabsTrigger>
            <TabsTrigger value="revision" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <Lightbulb className="h-4 w-4 mr-2" />
              Revision
            </TabsTrigger>
            <TabsTrigger value="mains" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <PenTool className="h-4 w-4 mr-2" />
              Mains Tips
            </TabsTrigger>
            <TabsTrigger value="quiz" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <HelpCircle className="h-4 w-4 mr-2" />
              Quiz
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <FileText className="h-5 w-5 text-emerald-500" />
                  Chapter Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                    {content.summary}
                  </p>
                </div>
                <p className="text-slate-400 text-sm mt-4">
                  Word count: {content.word_count}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Key Points Tab */}
          <TabsContent value="keypoints">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <List className="h-5 w-5 text-emerald-500" />
                  Key Points ({content.key_points?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.key_points?.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-slate-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Terms Tab */}
          <TabsContent value="terms">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <BookMarked className="h-5 w-5 text-emerald-500" />
                  Important Terms
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(content.important_terms || {}).map(([term, definition], idx) => (
                    <div key={idx} className="border-l-2 border-emerald-500 pl-4">
                      <h4 className="font-semibold text-slate-900 mb-1">{term}</h4>
                      <p className="text-slate-600 text-sm">{definition}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Timeline if available */}
            {content.timeline && content.timeline.length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm mt-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {content.timeline.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded whitespace-nowrap">
                          {item.year}
                        </span>
                        <span className="text-slate-700">{item.event}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Quick Revision Tab */}
          <TabsContent value="revision">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  Quick Revision Points
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {content.revision_points?.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            {/* UPSC Connection */}
            {upscRelevance.connection_to_current_affairs && (
              <Card className="bg-white border-slate-200 shadow-sm mt-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                    <Target className="h-5 w-5 text-orange-500" />
                    Current Affairs Connection
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">
                    {upscRelevance.connection_to_current_affairs}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Mains Answer Tips Tab */}
          <TabsContent value="mains">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <PenTool className="h-5 w-5 text-purple-500" />
                  Mains Answer Writing Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {content.mains_answer_hints?.map((hint, idx) => (
                    <li key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-slate-700">{hint}</p>
                    </li>
                  ))}
                </ul>
                {(!content.mains_answer_hints || content.mains_answer_hints.length === 0) && (
                  <p className="text-slate-400 italic">No mains tips available for this chapter.</p>
                )}
              </CardContent>
            </Card>
            
            {/* Previous Year Questions */}
            {upscRelevance.previous_year_questions && upscRelevance.previous_year_questions.length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm mt-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                    <Target className="h-5 w-5 text-orange-500" />
                    Previous Year Question Themes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {upscRelevance.previous_year_questions.map((q, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-orange-500">•</span>
                        <span className="text-slate-700">{q}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Essay Connection */}
            {upscRelevance.essay_connection && (
              <Card className="bg-white border-slate-200 shadow-sm mt-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                    <FileText className="h-5 w-5 text-cyan-500" />
                    Essay Connection
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{upscRelevance.essay_connection}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Quiz Tab */}
          <TabsContent value="quiz">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <HelpCircle className="h-5 w-5 text-orange-500" />
                  Chapter Quiz
                  <Badge className="bg-orange-100 text-orange-600 border-0 ml-2">
                    5 Questions
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Loading State */}
                {loadingQuiz && (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
                    <p className="text-slate-600">Generating quiz questions...</p>
                    <p className="text-slate-400 text-sm">This may take a few seconds</p>
                  </div>
                )}

                {/* Quiz Not Loaded Yet */}
                {!quiz && !loadingQuiz && (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <HelpCircle className="h-16 w-16 text-orange-300" />
                    <h3 className="text-xl font-semibold text-slate-900">Test Your Understanding</h3>
                    <p className="text-slate-500 text-center max-w-md">
                      Take a quick 5-question quiz to test your understanding of this chapter.
                      Wrong answers will be saved to your revision list.
                    </p>
                    <Button 
                      onClick={fetchQuiz}
                      className="bg-orange-500 hover:bg-orange-600 mt-4"
                    >
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Start Quiz
                    </Button>
                  </div>
                )}

                {/* Quiz Questions */}
                {quiz && !quizSubmitted && (
                  <div className="space-y-6">
                    {quiz.questions.map((question, qIdx) => (
                      <div key={qIdx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-start gap-3 mb-4">
                          <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {qIdx + 1}
                          </span>
                          <div className="flex-1">
                            <p className="text-slate-900 font-medium">{question.question_text}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                                {question.difficulty}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                                {question.topic_tag}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 ml-11">
                          {question.options.map((option, oIdx) => (
                            <button
                              key={oIdx}
                              onClick={() => setSelectedAnswers(prev => ({ ...prev, [qIdx]: oIdx }))}
                              className={`w-full text-left p-3 rounded-lg border transition-all ${
                                selectedAnswers[qIdx] === oIdx
                                  ? 'bg-orange-100 border-orange-500 text-slate-900'
                                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                              }`}
                            >
                              <span className="font-medium mr-2">
                                {String.fromCharCode(65 + oIdx)}.
                              </span>
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Submit Button */}
                    <div className="flex justify-center pt-4">
                      <Button
                        onClick={submitQuiz}
                        disabled={submittingQuiz || Object.keys(selectedAnswers).length < quiz.questions.length}
                        className="bg-orange-500 hover:bg-orange-600 px-8"
                      >
                        {submittingQuiz ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Submit Quiz
                      </Button>
                    </div>
                    
                    <p className="text-center text-slate-400 text-sm">
                      {Object.keys(selectedAnswers).length}/{quiz.questions.length} questions answered
                    </p>
                  </div>
                )}

                {/* Quiz Results */}
                {quizSubmitted && quizResults && (
                  <div className="space-y-6">
                    {/* Score Card */}
                    <div className={`p-6 rounded-lg text-center ${
                      quizResults.percentage >= 80 
                        ? 'bg-emerald-50 border border-emerald-200' 
                        : quizResults.percentage >= 60 
                          ? 'bg-yellow-50 border border-yellow-200'
                          : 'bg-red-50 border border-red-200'
                    }`}>
                      <Trophy className={`h-12 w-12 mx-auto mb-3 ${
                        quizResults.percentage >= 80 
                          ? 'text-emerald-500' 
                          : quizResults.percentage >= 60 
                            ? 'text-yellow-500'
                            : 'text-red-500'
                      }`} />
                      <h3 className="text-3xl font-bold text-slate-900 mb-1">
                        {quizResults.score}/{quizResults.total}
                      </h3>
                      <p className={`text-lg ${
                        quizResults.percentage >= 80 
                          ? 'text-emerald-600' 
                          : quizResults.percentage >= 60 
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}>
                        {quizResults.percentage}% 
                        {quizResults.percentage >= 80 ? ' - Excellent!' : 
                         quizResults.percentage >= 60 ? ' - Good Job!' : ' - Keep Practicing!'}
                      </p>
                      
                      {quizResults.weak_topics.length > 0 && (
                        <div className="mt-4 text-sm text-slate-500">
                          <p className="mb-2">Topics to revise:</p>
                          <div className="flex flex-wrap justify-center gap-2">
                            {quizResults.weak_topics.map((topic, idx) => (
                              <Badge key={idx} variant="outline" className="text-orange-600 border-orange-300">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {quizResults.mistakes_saved > 0 && (
                        <p className="mt-3 text-sm text-slate-400">
                          {quizResults.mistakes_saved} wrong answers saved to your revision list
                        </p>
                      )}
                    </div>

                    {/* Detailed Results */}
                    <div className="space-y-4">
                      <h4 className="text-slate-900 font-semibold">Detailed Review</h4>
                      {quizResults.results.map((result, idx) => (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border ${
                            result.is_correct 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {result.is_correct ? (
                              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1">
                              <p className="text-slate-900 font-medium mb-2">{result.question_text}</p>
                              
                              <div className="space-y-1 text-sm">
                                {result.options.map((opt, oIdx) => (
                                  <div 
                                    key={oIdx}
                                    className={`px-2 py-1 rounded ${
                                      oIdx === result.correct_answer
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : oIdx === result.user_answer && !result.is_correct
                                          ? 'bg-red-100 text-red-700 line-through'
                                          : 'text-slate-500'
                                    }`}
                                  >
                                    {String.fromCharCode(65 + oIdx)}. {opt}
                                    {oIdx === result.correct_answer && ' ✓'}
                                    {oIdx === result.user_answer && !result.is_correct && ' (Your answer)'}
                                  </div>
                                ))}
                              </div>
                              
                              <div className="mt-3 p-3 bg-slate-100 rounded text-sm">
                                <p className="text-slate-600">
                                  <span className="text-slate-700 font-medium">Explanation: </span>
                                  {result.explanation}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Retry Button */}
                    <div className="flex justify-center pt-4">
                      <Button
                        onClick={resetQuiz}
                        variant="outline"
                        className="border-orange-500 text-orange-500 hover:bg-orange-50"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Related Chapters Section */}
        {relatedChapters.length > 0 && (
          <Card className="bg-white border-slate-200 shadow-sm mt-8" data-testid="related-chapters">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                <Link2 className="h-5 w-5 text-cyan-500" />
                Related Chapters
                <span className="text-sm font-normal text-slate-500">
                  (Cross-linked for comprehensive UPSC preparation)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {relatedChapters.map((related, idx) => (
                  <Link
                    key={idx}
                    to={`/foundation/${related.subject.id}/book/${related.book.id}/chapter/${related.chapter_id}`}
                    className="group flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-cyan-300 hover:bg-white hover:shadow-sm transition-all"
                  >
                    {/* Subject indicator */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                      style={{ backgroundColor: `${related.subject.color}15` }}
                    >
                      {related.subject.icon}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant="outline" 
                          className="text-xs border-slate-300"
                          style={{ color: related.subject.color }}
                        >
                          {related.subject.name}
                        </Badge>
                        <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                          Class {related.book.class_num}
                        </Badge>
                      </div>
                      <h4 className="text-slate-900 font-medium group-hover:text-cyan-600 transition-colors truncate">
                        {related.title}
                      </h4>
                      <p className="text-slate-400 text-xs mt-1">
                        {related.match_reason}
                      </p>
                    </div>
                    
                    {/* Arrow */}
                    <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-cyan-500 transition-colors flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Loading Related */}
        {loadingRelated && (
          <div className="flex items-center justify-center gap-2 text-slate-500 mt-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Finding related chapters...</span>
          </div>
        )}

        {/* Mark Complete Button */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={markComplete}
            disabled={marking}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 px-8 py-3 text-white"
            data-testid="mark-complete-btn"
          >
            {marking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Mark as Complete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FoundationChapterPage;
